import {
  Button,
  Card,
  Column,
  Copy,
  Flex,
  Heading,
  Pill,
  Row,
  Square,
  Squish,
  Stack,
  SvgCaretRight,
  SvgGarbage,
  SvgPencil,
} from '@grubhubprod/gh-cookbook-react/dist';
import classNames from 'classnames';
import React from 'react';
import { LineOfCreditCardAssignPeople } from 'src/components/selfService/features/LineOfCredit/LineOfCreditCardAssignPeople';
import { Tooltip } from 'src/components/Tooltip/Tooltip';

export interface ILineOfCreditCardProp {
  isPhone: boolean;
  isActive: boolean;
  locName: string;
  locId: string;
  locBudgetDescription: string;
  isDisabled: boolean;
  onView?: (locId: string) => void;
  onEdit: (locId: string) => void;
  onRemove?: (locId: string) => void;
  hasDiners?: boolean | null;
}

export default function LineOfCreditCard({
  isPhone,
  isActive,
  isDisabled,
  locName,
  locBudgetDescription,
  locId,
  onView,
  onRemove,
  onEdit,
  hasDiners,
}: ILineOfCreditCardProp): JSX.Element {
  const shouldShowAssignPeopleMessage = hasDiners === false;

  function handleEdit(event) {
    event.stopPropagation();
    onEdit(locId);
  }
  function handleRemove(event) {
    event.stopPropagation();
    onRemove(locId);
  }

  function handleView() {
    if (isActive) {
      onView(locId);
    }
  }

  return (
    <Card
      data-testid="loc-card"
      className={classNames('lineOfCreditCard', { inactiveCard: !isActive })}
    >
      {!isPhone ? (
        <Squish inset="lg">
          <Flex justifyContent="space-between" alignItems="center">
            <Stack x="lg">
              <Flex flexDirection="column">
                <Square className="locInfo">
                  <Stack y="xxx-sm" className="cardHeader">
                    <Copy
                      variant="body"
                      color={isActive ? 'primaryText' : 'secondaryText'}
                    >
                      <Heading variant="h6">{locName}</Heading>
                    </Copy>
                  </Stack>
                </Square>
                <Square className="locInfo">
                  <Flex alignItems="center">
                    <Stack x="sm">
                      <Copy
                        variant="body"
                        color={isActive ? 'primaryText' : 'secondaryText'}
                      >
                        {locBudgetDescription}
                      </Copy>
                    </Stack>

                    {shouldShowAssignPeopleMessage && (
                      <Flex alignItems="center">
                        <Stack x="sm">
                          <Copy variant="body" color="secondaryText">
                            <b>·</b>
                          </Copy>
                        </Stack>
                        <LineOfCreditCardAssignPeople />
                      </Flex>
                    )}
                  </Flex>
                </Square>
              </Flex>
            </Stack>
            {isActive ? (
              <Flex alignItems="center" flexShrink={0}>
                <Stack x="md">
                  <Tooltip
                    roundedContainer
                    content={
                      <Copy variant="body" color="invertedPrimaryText">
                        Edit
                      </Copy>
                    }
                  >
                    <div>
                      <Button
                        disabled={isDisabled}
                        variant="tertiary"
                        className="cardActionButtons"
                        onClick={handleEdit}
                      >
                        <Copy
                          aria-label="Edit"
                          variant="body"
                          color="interactive"
                        >
                          <SvgPencil size="24px" aria-label="Edit" />
                        </Copy>
                      </Button>
                    </div>
                  </Tooltip>
                </Stack>
                <Stack x="md">
                  <Tooltip
                    roundedContainer
                    content={
                      <Copy variant="body" color="invertedPrimaryText">
                        Delete
                      </Copy>
                    }
                  >
                    <div>
                      <Button
                        disabled={isDisabled}
                        variant="tertiary"
                        className="cardActionButtons"
                        onClick={handleRemove}
                      >
                        <Copy variant="body" color="interactive">
                          <SvgGarbage size="24px" aria-label="Delete" />
                        </Copy>
                      </Button>
                    </div>
                  </Tooltip>
                </Stack>

                <Tooltip
                  roundedContainer
                  content={
                    <Copy variant="body" color="invertedPrimaryText">
                      View details
                    </Copy>
                  }
                >
                  <div>
                    <Button
                      disabled={isDisabled}
                      variant="tertiary"
                      className="cardActionButtons"
                      onClick={handleView}
                    >
                      <Copy variant="body" color="interactive">
                        <SvgCaretRight size="24px" aria-label="View details" />
                      </Copy>
                    </Button>
                  </div>
                </Tooltip>
              </Flex>
            ) : (
              <Flex alignItems="center">
                <Pill className="deactivatedPill">
                  <Copy variant="body" color="primaryText">
                    Deactivated
                  </Copy>
                </Pill>
              </Flex>
            )}
          </Flex>
        </Squish>
      ) : (
        <Squish inset="lg">
          <Flex alignItems="center" flexDirection="column">
            <Stack y="sm" width="100%">
              <Flex alignItems="center" justifyContent="space-between">
                <Stack x="lg">
                  <Flex flexDirection="column">
                    <Square className="locInfo">
                      <Stack y="xxx-sm" className="cardHeader">
                        <Copy
                          variant="body"
                          color={isActive ? 'primaryText' : 'secondaryText'}
                        >
                          <Heading variant="h6">{locName}</Heading>
                        </Copy>
                      </Stack>
                    </Square>
                    <Square className="locInfo">
                      <Copy
                        variant="body"
                        color={isActive ? 'primaryText' : 'secondaryText'}
                      >
                        {locBudgetDescription}
                      </Copy>

                      {shouldShowAssignPeopleMessage && (
                        <Stack>
                          <LineOfCreditCardAssignPeople />
                        </Stack>
                      )}
                    </Square>
                  </Flex>
                </Stack>
                {isActive && (
                  <Button
                    variant="tertiary"
                    className="cardActionButtons"
                    onClick={handleView}
                  >
                    <Copy variant="body" color="interactive">
                      <SvgCaretRight size="24px" aria-label="View details" />
                    </Copy>
                  </Button>
                )}
              </Flex>
            </Stack>
            {isActive ? (
              <Stack width="100%">
                <Row gutter={0} className="cardActionButtonsRowMobile">
                  <Column gutter={0} phone={6}>
                    <Button
                      block
                      variant="tertiary"
                      buttonSize="medium"
                      disabled={isDisabled}
                      onClick={handleEdit}
                    >
                      <Copy variant="body" color="interactive">
                        <SvgPencil size="24px" /> Edit
                      </Copy>
                    </Button>
                  </Column>
                  <Column gutter={0} phone={6}>
                    <Button
                      disabled={isDisabled}
                      block
                      variant="tertiary"
                      buttonSize="medium"
                      className="cardActionButtonRightMobile"
                      onClick={handleRemove}
                    >
                      <Copy variant="body" color="interactive">
                        <SvgGarbage size="24px" /> Delete
                      </Copy>
                    </Button>
                  </Column>
                </Row>
              </Stack>
            ) : (
              <Flex alignItems="center" justifyItems="flex-start" width="100%">
                <Pill className="deactivatedPill">
                  <Copy variant="body" color="primaryText">
                    Deactivated
                  </Copy>
                </Pill>
              </Flex>
            )}
          </Flex>
        </Squish>
      )}
    </Card>
  );
}
